import { ArrowLeft, LoaderCircle, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import FormularioVentaPos, {
  Campo,
} from '../../components/pos/FormularioVentaPos'
import GuardarCambiosVentaPosModal from '../../components/pos/GuardarCambiosVentaPosModal'
import ModalMensaje from '../../components/ui/ModalMensaje'
import { useAuth } from '../../context/AuthContext'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import { formatearMedioPago } from '../../utils/gastos'
import { normalizarMoneda, redondearMoneda } from '../../utils/numeros'
import {
  ESTADOS_ENVASES_POS,
  OPCIONES_MEDIO_PAGO_POS,
  formatearFechaPos,
  formatearKilosPos,
  formatearMontoPos,
  obtenerEstadoPagoNuevaVentaPos,
} from '../../utils/pos'

// Medios que además de monto admiten una referencia (N.º de
// transferencia o de cheque). Igual criterio que en Nueva venta.
const MEDIOS_CON_REFERENCIA = ['transferencia', 'cheque']

// Tolerancia mínima solo para la comparación en el frontend (nunca para
// el backend, que sigue siendo la autoridad con numeric(16,2)). Cubre
// residuos de punto flotante que puedan quedar pese al redondeo por
// línea; no oculta diferencias reales de un peso o más.
const TOLERANCIA_COMPARACION_MONTOS = 0.001

// Monto de pago: dinero, no decimal físico — un punto es separador de
// miles ("30.000" -> 30000). Ver normalizarMoneda.
function convertirMonto(valor) {
  const numero = normalizarMoneda(valor)

  return Number.isFinite(numero) ? numero : 0
}

function crearPagoVacio(idLocal) {
  return {
    idLocal,
    medio_pago: '',
    monto: '',
    referencia: '',
  }
}

// Espejo únicamente informativo de las reglas que ya aplica
// `editar_venta_pos` en Supabase. Sirve para explicarle al usuario por qué
// no puede editar antes de intentarlo; Supabase sigue siendo la autoridad
// final si el estado cambia por concurrencia.
function razonNoEditable(venta, { esAdministrador, usuarioId }) {
  if (!venta) {
    return 'La venta no existe o no pertenece a esta empresa.'
  }

  if (venta.estado === 'anulado') {
    return 'Esta venta fue eliminada y no puede editarse.'
  }

  if (venta.estado !== 'registrado') {
    return 'Esta venta no puede editarse en su estado actual.'
  }

  if (!esAdministrador && venta.creado_por !== usuarioId) {
    return 'Solo puedes editar tus propias ventas.'
  }

  return ''
}

function EditarVentaPos() {
  const { ventaId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, cerrarModal } = useModalMensaje()

  const esConsulta = empresaActual?.rol === 'consulta'
  const esAdministrador = empresaActual?.rol === 'administrador'

  const [venta, setVenta] = useState(null)
  const [detalles, setDetalles] = useState([])
  const [pagosCargados, setPagosCargados] = useState([])
  const [pagos, setPagos] = useState([])
  const [cargando, setCargando] = useState(true)

  const [totales, setTotales] = useState({
    subtotalVenta: 0,
    montoDescuento: 0,
    totalVenta: 0,
    totalKilos: 0,
  })

  const [datosPendientes, setDatosPendientes] = useState(null)
  const [resumenGuardar, setResumenGuardar] = useState(null)
  const [modalGuardarAbierto, setModalGuardarAbierto] =
    useState(false)
  const [guardando, setGuardando] = useState(false)

  const montadoRef = useRef(true)
  const contadorPagoRef = useRef(0)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    cargarVenta()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ventaId, empresaActual?.id])

  // Precarga los pagos editables desde los pagos activos ya registrados
  // apenas se cargan (una sola vez por venta abierta).
  useEffect(() => {
    contadorPagoRef.current = 0

    setPagos(
      pagosCargados.map((pago) => {
        contadorPagoRef.current += 1

        return {
          idLocal: contadorPagoRef.current,
          medio_pago: pago.medio_pago,
          monto: String(pago.monto ?? ''),
          referencia: pago.referencia || '',
        }
      })
    )
  }, [pagosCargados])

  // Si el total de la venta queda en $0 (p. ej. descuento del 100%), no
  // tiene sentido mantener pagos armados: se limpian para no enviarlos.
  useEffect(() => {
    if (totales.totalVenta <= 0 && pagos.length > 0) {
      setPagos([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totales.totalVenta])

  const cargarVenta = async () => {
    if (!ventaId || !empresaActual?.id) return

    try {
      setCargando(true)

      const [respuestaVenta, respuestaDetalle, respuestaPagos] =
        await Promise.all([
          supabase
            .from('vw_ventas_pos_resumen')
            .select('*')
            .eq('id', ventaId)
            .eq('empresa_id', empresaActual.id)
            .maybeSingle(),
          supabase
            .from('vw_ventas_pos_detalle')
            .select('*')
            .eq('venta_id', ventaId)
            .eq('empresa_id', empresaActual.id)
            .order('creado_en', { ascending: true }),
          supabase
            .from('vw_ventas_pos_pagos')
            .select('*')
            .eq('venta_id', ventaId)
            .eq('empresa_id', empresaActual.id)
            .eq('estado', 'registrado')
            .order('fecha_pago', { ascending: true }),
        ])

      if (respuestaVenta.error) throw respuestaVenta.error
      if (respuestaDetalle.error) throw respuestaDetalle.error
      if (respuestaPagos.error) throw respuestaPagos.error

      if (!montadoRef.current) return

      const ventaCargada = respuestaVenta.data
      const razon = razonNoEditable(ventaCargada, {
        esAdministrador,
        usuarioId: user?.id,
      })

      if (razon) {
        setVenta(null)
        setDetalles([])
        setPagosCargados([])

        abrirModal({
          tipo: 'error',
          titulo: 'No es posible editar esta venta',
          mensaje: razon,
          onAceptar: () => {
            cerrarModal()
            navigate('/pos/ventas')
          },
        })
        return
      }

      setVenta(ventaCargada)
      setDetalles(respuestaDetalle.data || [])
      setPagosCargados(respuestaPagos.data || [])
    } catch (errorCarga) {
      console.error('Error al cargar la venta POS:', errorCarga)

      if (!montadoRef.current) return

      setVenta(null)
      setDetalles([])
      setPagosCargados([])

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar la venta',
        mensaje:
          errorCarga.message || 'No fue posible cargar la venta.',
        onAceptar: () => {
          cerrarModal()
          navigate('/pos/ventas')
        },
      })
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const valoresIniciales = useMemo(() => {
    if (!venta) return null

    return {
      fecha: venta.fecha_venta,
      clienteId: venta.cliente_id || '',
      observaciones: venta.observaciones || '',
      estadoEnvases: venta.estado_envases || 'pendiente',
      montoEnvasesPagado:
        venta.estado_envases === 'pagado'
          ? String(venta.monto_envases_pagado ?? '')
          : '',
      tipoDescuento: venta.tipo_descuento || 'ninguno',
      valorDescuento:
        venta.tipo_descuento && venta.tipo_descuento !== 'ninguno'
          ? String(venta.valor_descuento ?? '')
          : '',
      filas: detalles.map((detalle) => ({
        productoId: detalle.producto_id,
        tipoEnvaseId: detalle.tipo_envase_id,
        cantidad: String(detalle.cantidad ?? ''),
        pesoBruto: String(detalle.peso_bruto_kg ?? ''),
        valorKilo: String(detalle.valor_kilo ?? ''),
      })),
    }
  }, [venta, detalles])

  const pagosConMonto = pagos.map((pago) => ({
    ...pago,
    montoNumero: convertirMonto(pago.monto),
  }))

  const totalPagado = redondearMoneda(
    pagosConMonto.reduce(
      (acumulado, pago) => acumulado + pago.montoNumero,
      0
    )
  )

  const saldo = totales.totalVenta - totalPagado

  const estadoPagoActual = obtenerEstadoPagoNuevaVentaPos(
    totales.totalVenta,
    totalPagado
  )

  const pagosExceden =
    totales.totalVenta > 0 &&
    totalPagado > totales.totalVenta + TOLERANCIA_COMPARACION_MONTOS

  const siguienteIdPago = () => {
    contadorPagoRef.current += 1
    return contadorPagoRef.current
  }

  const agregarPago = () => {
    if (totales.totalVenta <= 0) return

    setPagos((actuales) => [
      ...actuales,
      crearPagoVacio(siguienteIdPago()),
    ])
  }

  const eliminarPago = (idLocal) => {
    setPagos((actuales) =>
      actuales.filter((pago) => pago.idLocal !== idLocal)
    )
  }

  const cambiarCampoPago = (idLocal, campo, valor) => {
    setPagos((actuales) =>
      actuales.map((pago) =>
        pago.idLocal === idLocal ? { ...pago, [campo]: valor } : pago
      )
    )
  }

  const manejarValidSubmit = (datos) => {
    if (datos.totalVenta > 0) {
      for (const pago of pagosConMonto) {
        if (!pago.medio_pago) {
          abrirModal({
            tipo: 'error',
            titulo: 'Medio de pago requerido',
            mensaje:
              'Debes seleccionar el medio de pago en cada pago agregado.',
          })
          return
        }

        if (pago.montoNumero <= 0) {
          abrirModal({
            tipo: 'error',
            titulo: 'Monto inválido',
            mensaje:
              'El monto de cada pago debe ser mayor que cero.',
          })
          return
        }
      }

      if (
        totalPagado >
        datos.totalVenta + TOLERANCIA_COMPARACION_MONTOS
      ) {
        abrirModal({
          tipo: 'error',
          titulo: 'Pagos superan el total',
          mensaje:
            'El total de los pagos no puede superar el total de la venta.',
        })
        return
      }
    }

    setDatosPendientes(datos)

    setResumenGuardar({
      clienteLabel:
        datos.clienteSeleccionado?.razon_social ||
        'Venta sin cliente',
      cantidadProductos: datos.filas.length,
      totalKilosLabel: formatearKilosPos(datos.totalKilos),
      subtotalLabel: formatearMontoPos(datos.subtotalVenta),
      descuento:
        datos.tipoDescuento === 'ninguno'
          ? null
          : {
              etiqueta:
                datos.tipoDescuento === 'porcentaje'
                  ? `Descuento ${datos.valorDescuento || 0}%`
                  : 'Descuento',
              montoLabel: `- ${formatearMontoPos(
                datos.montoDescuento
              )}`,
            },
      totalVentaLabel: formatearMontoPos(datos.totalVenta),
      estadoEnvasesLabel:
        ESTADOS_ENVASES_POS[datos.estadoEnvases].etiqueta,
      montoEnvasesPagadoLabel:
        datos.estadoEnvases === 'pagado'
          ? formatearMontoPos(datos.montoEnvasesPagadoNumero)
          : null,
      pagos: pagosConMonto.map((pago) => ({
        idLocal: pago.idLocal,
        medioLabel: formatearMedioPago(pago.medio_pago),
        montoLabel: formatearMontoPos(pago.montoNumero),
        referencia: pago.referencia.trim() || null,
      })),
      totalPagadoLabel: formatearMontoPos(totalPagado),
      saldoLabel: formatearMontoPos(
        Math.max(datos.totalVenta - totalPagado, 0)
      ),
    })

    setModalGuardarAbierto(true)
  }

  const guardarCambios = async (motivo) => {
    if (guardando || !datosPendientes) return

    try {
      setGuardando(true)

      const payload = {
        p_empresa_id: empresaActual.id,
        p_venta_id: ventaId,
        p_cliente_id: datosPendientes.clienteId || null,
        p_fecha_venta: datosPendientes.fecha,
        p_estado_envases: datosPendientes.estadoEnvases,
        p_monto_envases_pagado: datosPendientes.montoEnvasesPagadoNumero,
        p_tipo_descuento: datosPendientes.tipoDescuento,
        p_valor_descuento:
          datosPendientes.tipoDescuento === 'ninguno'
            ? 0
            : datosPendientes.valorDescuentoNumero,
        p_observaciones:
          datosPendientes.observaciones.trim() || null,
        p_detalles: datosPendientes.detallesPayload,
        p_motivo: motivo,
        p_pagos:
          datosPendientes.totalVenta > 0
            ? pagosConMonto.map((pago) => ({
                fecha_pago: null,
                medio_pago: pago.medio_pago,
                monto: pago.montoNumero,
                referencia: pago.referencia.trim() || null,
                observaciones: null,
              }))
            : [],
      }

      const { error } = await supabase.rpc(
        'editar_venta_pos',
        payload
      )

      if (error) throw error

      setModalGuardarAbierto(false)

      abrirModal({
        tipo: 'success',
        titulo: 'Venta actualizada',
        mensaje: 'Venta actualizada correctamente.',
        onAceptar: () => {
          cerrarModal()
          navigate('/pos/ventas', { state: { recargar: true } })
        },
      })
    } catch (errorGuardar) {
      console.error('Error al editar venta POS:', errorGuardar)

      setModalGuardarAbierto(false)

      abrirModal({
        tipo: 'error',
        titulo: 'No fue posible guardar los cambios',
        mensaje:
          errorGuardar.message ||
          'No fue posible guardar los cambios de la venta.',
      })
    } finally {
      setGuardando(false)
    }
  }

  if (esConsulta) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <h3 className="text-lg font-bold text-slate-900">
          Sin permisos
        </h3>

        <p className="mt-2 text-slate-500">
          Tu perfil no tiene permisos para editar ventas.
        </p>
      </div>
    )
  }

  if (cargando) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando venta...
          </p>
        </div>
      </div>
    )
  }

  if (!venta) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => navigate('/pos/ventas')}
          className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
        >
          <ArrowLeft size={18} />
          Ventas
        </button>

        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <p className="text-slate-500">
            No fue posible mostrar esta venta.
          </p>
        </div>

        <ModalMensaje {...modal} />
      </div>
    )
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => navigate('/pos/ventas')}
        className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Ventas
      </button>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          {empresaActual?.nombre_fantasia}
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          Editar venta
        </h2>

        <p className="mt-2 text-slate-500">
          Venta del {formatearFechaPos(venta.fecha_venta)}
        </p>
      </div>

      <FormularioVentaPos
        empresaId={empresaActual?.id}
        valoresIniciales={valoresIniciales}
        bloqueado={guardando}
        deshabilitarGuardar={pagosExceden}
        textoBoton="Guardar cambios"
        iconoBoton={Save}
        onCancelar={() => navigate('/pos/ventas')}
        onValidSubmit={manejarValidSubmit}
        onCambiarTotales={setTotales}
      >
        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Pagos
            </h3>

            {totales.totalVenta > 0 && (
              <button
                type="button"
                onClick={agregarPago}
                disabled={guardando}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <Plus size={18} />
                Agregar forma de pago
              </button>
            )}
          </div>

          {totales.totalVenta <= 0 ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              <p className="font-semibold text-slate-700">
                Venta sin monto a pagar
              </p>
              <p className="mt-1">
                Total venta {formatearMontoPos(totales.totalVenta)}
              </p>
            </div>
          ) : (
            <>
              {pagos.length === 0 && (
                <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  Sin pagos. La venta quedará pendiente de pago.
                </p>
              )}

              <div className="space-y-4">
                {pagos.map((pago, indice) => {
                  const mostrarReferencia =
                    MEDIOS_CON_REFERENCIA.includes(pago.medio_pago)

                  return (
                    <div
                      key={pago.idLocal}
                      className="rounded-xl border border-slate-200 p-4"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                          Pago {indice + 1}
                        </p>

                        <button
                          type="button"
                          onClick={() =>
                            eliminarPago(pago.idLocal)
                          }
                          disabled={guardando}
                          title="Eliminar pago"
                          aria-label="Eliminar pago"
                          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-red-200 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Campo label="Medio de pago" requerido>
                          <select
                            value={pago.medio_pago}
                            onChange={(event) =>
                              cambiarCampoPago(
                                pago.idLocal,
                                'medio_pago',
                                event.target.value
                              )
                            }
                            disabled={guardando}
                            className={estiloInput}
                          >
                            <option value="">
                              Seleccionar medio
                            </option>

                            {OPCIONES_MEDIO_PAGO_POS.map(
                              (opcion) => (
                                <option
                                  key={opcion.valor}
                                  value={opcion.valor}
                                >
                                  {opcion.etiqueta}
                                </option>
                              )
                            )}
                          </select>
                        </Campo>

                        <Campo label="Monto" requerido>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={pago.monto}
                            onChange={(event) =>
                              cambiarCampoPago(
                                pago.idLocal,
                                'monto',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={guardando}
                            className={estiloInput}
                          />
                        </Campo>

                        {mostrarReferencia && (
                          <div className="sm:col-span-2">
                            <Campo
                              label={
                                pago.medio_pago === 'cheque'
                                  ? 'N° cheque / referencia'
                                  : 'Referencia (opcional)'
                              }
                            >
                              <input
                                type="text"
                                value={pago.referencia}
                                onChange={(event) =>
                                  cambiarCampoPago(
                                    pago.idLocal,
                                    'referencia',
                                    event.target.value
                                  )
                                }
                                placeholder={
                                  pago.medio_pago === 'cheque'
                                    ? 'Ej: CH-001245'
                                    : 'Ej: TRX-123456'
                                }
                                disabled={guardando}
                                className={estiloInput}
                              />
                            </Campo>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <ResumenPago
                    etiqueta="Nuevo total venta"
                    valor={formatearMontoPos(totales.totalVenta)}
                  />

                  <ResumenPago
                    etiqueta="Total pagado"
                    valor={formatearMontoPos(totalPagado)}
                  />

                  <ResumenPago
                    etiqueta="Saldo pendiente"
                    valor={formatearMontoPos(Math.max(saldo, 0))}
                    alerta={pagosExceden}
                  />
                </div>

                <p
                  className={`mt-4 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estadoPagoActual.clase}`}
                >
                  {estadoPagoActual.etiqueta}
                </p>

                {pagosExceden && (
                  <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    El total de los pagos no puede superar el total
                    de la venta.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </FormularioVentaPos>

      <GuardarCambiosVentaPosModal
        abierto={modalGuardarAbierto}
        resumen={resumenGuardar}
        procesando={guardando}
        onCancelar={() => setModalGuardarAbierto(false)}
        onConfirmar={guardarCambios}
      />

      <ModalMensaje {...modal} />
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

function ResumenPago({ etiqueta, valor, alerta = false }) {
  return (
    <div
      className={`rounded-lg p-3 text-center shadow-sm ${
        alerta ? 'bg-red-50' : 'bg-white'
      }`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {etiqueta}
      </p>

      <p
        className={`mt-1 text-sm font-bold ${
          alerta ? 'text-red-700' : 'text-slate-900'
        }`}
      >
        {valor}
      </p>
    </div>
  )
}

export default EditarVentaPos
